/**
 * useAudit — EO «Аудит»: лента audit_log + сводки (V1 AuditView,
 * index.html:15300-15510). Два endpoint'а (perm audit:read):
 *  • GET /api/audit?limit=50&offset&from&to&staff&action&entity_type&attention=1
 *    — offset-пагинация; staff матчит staff_id точно ИЛИ staff_name LIKE;
 *    action — LIKE по action ИЛИ detail; ⚠ wire-имя фильтра сущности —
 *    именно `entity_type`. view_role НЕ передаём: EO должен видеть и
 *    EO-активность (hideEO срабатывает только для не-EO / view_role=oo).
 *  • GET /api/audit/stats?from&to — byStaff/byAction/byEntity/attention.
 * ⚠ created_at — UTC sqlite «YYYY-MM-DD HH:MM:SS» БЕЗ Z; парсинг и сдвиг
 * +5ч — на клиенте (fmtAuditTs в EoAuditView). attention — число 0/1.
 * Текстовые фильтры (staff/action) дебаунсятся 300 мс.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

export interface AuditRow {
  id: number;
  staff_id: string | null;
  staff_name: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  detail: string | null;
  meta: string | null;
  attention: number;
  created_at: string;
}
export interface AuditStats {
  byStaff: { staff_name: string | null; staff_id: string | null; cnt: number }[];
  byAction: { action: string; cnt: number }[];
  byEntity: { entity_type: string | null; cnt: number }[];
  attention: number;
}
export interface AuditFilters { staff: string; action: string; entity: string; attentionOnly: boolean }

const PAGE = 50;
interface LogResp { results?: AuditRow[]; total?: number }

export interface AuditResult {
  log: AuditRow[];
  total: number;
  stats: AuditStats | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  filters: AuditFilters;
  setFilters: (patch: Partial<AuditFilters>) => void;
  loadMore: () => void;
  reload: () => void;
}

export function useAudit(from: string, to: string): AuditResult {
  const [filters, setFiltersState] = useState<AuditFilters>({ staff: "", action: "", entity: "", attentionOnly: false });
  const [deb, setDeb] = useState({ staff: "", action: "" });
  const [log, setLog] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDeb({ staff: filters.staff.trim(), action: filters.action.trim() }), 300);
    return () => clearTimeout(t);
  }, [filters.staff, filters.action]);

  const query = useCallback((offset: number): string => {
    const p = new URLSearchParams({ limit: String(PAGE), offset: String(offset), from, to });
    if (deb.staff) p.set("staff", deb.staff);
    if (deb.action) p.set("action", deb.action);
    if (filters.entity) p.set("entity_type", filters.entity);
    if (filters.attentionOnly) p.set("attention", "1");
    return p.toString();
  }, [from, to, deb.staff, deb.action, filters.entity, filters.attentionOnly]);

  /* Смена периода/фильтров → страница 0 + сводка параллельно. */
  useEffect(() => {
    if (!from || !to) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    Promise.all([
      api<LogResp>(`/api/audit?${query(0)}`, { signal: ac.signal }),
      api<AuditStats>(`/api/audit/stats?from=${from}&to=${to}`, { signal: ac.signal }).catch(() => null),
    ])
      .then(([r, s]) => {
        if (ac.signal.aborted) return;
        setLog(r?.results ?? []);
        setTotal(r?.total ?? 0);
        setStats(s);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setLog([]);
        setTotal(0);
        setError(e instanceof Error ? e.message : "Не удалось загрузить аудит");
        setLoading(false);
      });
    return () => ac.abort();
  }, [from, to, query, trigger]);

  const loadMore = useCallback(() => {
    if (loadingMore || log.length >= total) return;
    setLoadingMore(true);
    api<LogResp>(`/api/audit?${query(log.length)}`)
      .then((r) => {
        setLog((cur) => [...cur, ...(r?.results ?? [])]);
        if (typeof r?.total === "number") setTotal(r.total);
      })
      .catch(() => { /* страница не доехала — кнопка «Ещё» останется */ })
      .finally(() => setLoadingMore(false));
  }, [query, log.length, total, loadingMore]);

  const setFilters = useCallback((patch: Partial<AuditFilters>) => {
    setFiltersState((f) => ({ ...f, ...patch }));
  }, []);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  return { log, total, stats, loading, loadingMore, error, filters, setFilters, loadMore, reload };
}
